import { supabase } from '../supabase/config';
import { logActivity } from '../utils/activityLogger';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole } from '../utils/loyalty';

/**
 * Maps a snake_case DB row to camelCase order object.
 */
function toOrder(row) {
    if (!row) return null;
    return {
        id: row.id,
        tenantId: row.tenant_id,
        customerId: row.customer_id,
        customerName: row.customer_name,
        tableNumber: row.table_number,
        status: row.status,
        items: row.items || [],
        totalAmount: Number(row.total_amount || 0),
        remainingAmount: row.remaining_amount == null ? Number(row.total_amount || 0) : Number(row.remaining_amount),
        payments: row.payments || [],
        earnedPoints: Number(row.earned_points || 0),
        subtotal: row.subtotal == null ? Number(row.total_amount || 0) : Number(row.subtotal),
        discountAmount: Number(row.discount_amount || 0),
        appliedPromotionId: row.applied_promotion_id || null,
        appliedPromotionTitle: row.applied_promotion_title || null,
        createdBy: row.created_by,
        timestamp: row.timestamp,
        updatedAt: row.updated_at,
        paidAt: row.paid_at,
    };
}

export const orderService = {
    subscribeToOrders(tenantId, callback) {
        if (!tenantId) return () => { };

        // Initial fetch
        supabase
            .from('orders')
            .select('*')
            .eq('tenant_id', tenantId)
            .order('timestamp', { ascending: false })
            .then(({ data, error }) => {
                if (error) {
                    console.error("Order Subscription Error:", error);
                    callback([]);
                } else {
                    callback((data || []).map(toOrder));
                }
            });

        // Real-time subscription
        const channel = supabase
            .channel('orders-' + tenantId)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'orders', filter: `tenant_id=eq.${tenantId}` },
                () => {
                    supabase
                        .from('orders')
                        .select('*')
                        .eq('tenant_id', tenantId)
                        .order('timestamp', { ascending: false })
                        .then(({ data }) => {
                            if (data) callback(data.map(toOrder));
                        });
                }
            )
            .subscribe();

        return () => supabase.removeChannel(channel);
    },

    async updateOrderStatus(order, newStatus, tenantId, userId, userEmail, tenantData) {
        const orderId = order.id;
        const now = new Date().toISOString();
        const updateData = {
            status: newStatus,
            updated_at: now,
        };

        if (newStatus === 'paid') {
            updateData.paid_at = now;
        }

        // When moving to 'delivered', merge this order into an existing delivered
        // order for the same table + customer (so the "Teslim Edildi" list shows
        // one row per customer per table). Guests with no customer_id are NOT
        // merged, since we can't distinguish between different walk-in customers.
        if (
            newStatus === 'delivered' &&
            order.status !== 'delivered' &&
            order.tableNumber &&
            order.tableNumber !== 'Manuel' &&
            order.customerId
        ) {
            const { data: existingRows } = await supabase
                .from('orders')
                .select('*')
                .eq('tenant_id', tenantId)
                .eq('table_number', order.tableNumber)
                .eq('customer_id', order.customerId)
                .eq('status', 'delivered')
                .neq('id', orderId)
                .limit(1);

            const existingDelivered = existingRows && existingRows.length > 0 ? existingRows[0] : null;
            if (existingDelivered) {
                const mergedItems = [...(existingDelivered.items || [])];
                (order.items || []).forEach(newItem => {
                    const match = mergedItems.find(i => i.prodId === newItem.prodId);
                    if (match) {
                        match.quantity += newItem.quantity;
                    } else {
                        mergedItems.push(newItem);
                    }
                });
                const addedAmount = Number(order.totalAmount || 0);
                const newTotal = Number(existingDelivered.total_amount || 0) + addedAmount;
                const baseRemaining = existingDelivered.remaining_amount == null
                    ? Number(existingDelivered.total_amount || 0)
                    : Number(existingDelivered.remaining_amount);
                const newRemaining = baseRemaining + addedAmount;
                const newEarnedPoints = Number(existingDelivered.earned_points || 0) + Number(order.earnedPoints || 0);

                const { error: mergeErr } = await supabase
                    .from('orders')
                    .update({
                        items: mergedItems,
                        total_amount: newTotal,
                        remaining_amount: newRemaining,
                        earned_points: newEarnedPoints,
                        updated_at: now,
                    })
                    .eq('id', existingDelivered.id);
                if (mergeErr) throw mergeErr;

                const { error: delErr } = await supabase.from('orders').delete().eq('id', orderId);
                if (delErr) throw delErr;

                await logActivity(tenantId, userEmail, userId, 'MERGE_ORDER_ON_DELIVER', `Order ${orderId} merged into ${existingDelivered.id}`, {
                    mergedFrom: orderId,
                    mergedInto: existingDelivered.id,
                    tableNumber: order.tableNumber,
                    customerId: order.customerId,
                });
                return;
            }
        }

        // If marking as paid, process loyalty points and accounting
        // Re-fetch current order from DB to get latest status and customer_id (double-click guard + link guard)
        let freshOrder = null;
        if (newStatus === 'paid') {
            const { data } = await supabase.from('orders').select('status, customer_id, total_amount').eq('id', orderId).single();
            freshOrder = data;
            if (freshOrder?.status === 'paid') return; // Already paid, skip
        }
        if (newStatus === 'paid' && order.status !== 'paid') {
            const rules = tenantData?.loyaltyRules || tenantData?.loyalty_rules || {};
            const effectiveCustomerId = freshOrder?.customer_id || order.customerId;
            const effectiveTotalAmount = parseFloat(freshOrder?.total_amount) || order.totalAmount;
            const points = order.earnedPoints !== undefined
                ? order.earnedPoints
                : (rules.purchasePointsEnabled !== false
                    ? Math.floor(effectiveTotalAmount * (rules.purchasePointsMultiplier || 0.1))
                    : 0);

            // 2. Update customer points, total spend, and lifetime points
            if (effectiveCustomerId) {
                const { data: customerData } = await supabase
                    .from('customers')
                    .select('*')
                    .eq('id', effectiveCustomerId)
                    .single();

                if (customerData) {
                    const nowDate = new Date();
                    const twoYearsAgo = new Date();
                    twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);

                    const updatedPointEarnings = [
                        ...(customerData.point_earnings || []).filter(e => new Date(e.t) > twoYearsAgo),
                        { p: points, t: nowDate.toISOString() }
                    ];

                        const updatedCustomerFields = {
                            points: (customerData.points || 0) + points,
                            total_points: (customerData.total_points || 0) + points,
                            total_spent: (customerData.total_spent || 0) + effectiveTotalAmount,
                            purchase_count: (customerData.purchase_count || 0) + 1,
                            interaction_count: (customerData.interaction_count || 0) + 1,
                            visit_timestamps: [...(customerData.visit_timestamps || []), now],
                            point_earnings: updatedPointEarnings,
                            last_interaction_at: now,
                        };

                        // Helper to map snake_case to camelCase for calculation functions
                        const calculatedData = {
                            points: updatedCustomerFields.points,
                            totalPoints: updatedCustomerFields.total_points,
                            pointEarnings: updatedCustomerFields.point_earnings,
                            visitTimestamps: updatedCustomerFields.visit_timestamps,
                            purchaseCount: updatedCustomerFields.purchase_count
                        };

                        const newLevel = calculateLoyaltyLevel(calculatedData, tenantData);
                        const newRole = calculateVisitFrequencyRole(calculatedData, tenantData);

                        updatedCustomerFields.loyalty_level = newLevel?.label || null;
                        updatedCustomerFields.visit_role = newRole?.label || null;

                        const { error: custErr } = await supabase
                            .from('customers')
                            .update(updatedCustomerFields)
                            .eq('id', effectiveCustomerId);
                        if (custErr) console.error('[orderService] Customer Update Error:', custErr, updatedCustomerFields);
                }
            }

            // 3. Create Sales (Accounting) Record
            const { error: salesError } = await supabase.from('sales').insert({
                tenant_id: tenantId,
                order_id: order.id,
                customer_id: effectiveCustomerId || null,
                customer_name: order.customerName || 'Guest',
                amount: effectiveTotalAmount,
                points_earned: points,
                items: order.items,
                timestamp: now,
                type: 'Order',
            });
            
            if (salesError) {
                console.error('[orderService] Sales Record Error:', salesError);
            }

            // Write earned points and customer_id back to order
            updateData.earned_points = points;
            if (effectiveCustomerId) {
                updateData.customer_id = effectiveCustomerId;
            }
        }

        // 4. Update the order itself
        const { error: orderError } = await supabase.from('orders').update(updateData).eq('id', orderId);
        
        if (orderError) {
            console.error('[orderService] Order Update Error (400?):', orderError);
            throw orderError;
        }

        await logActivity(tenantId, userEmail, userId, 'UPDATE_ORDER_STATUS', `Order ${orderId} changed to ${newStatus}`, { 
            orderId, 
            previousStatus: order.status, 
            newStatus,
            amount: order.totalAmount,
            customerName: order.customerName,
            items: order.items 
        });
    },

    async updateOrderDetails(orderId, orderData, tenantId, userId, userEmail) {
        if (!orderId) return;
        const updatePayload = {};
        // Map camelCase input keys to snake_case DB columns
        const keyMap = {
            totalAmount: 'total_amount',
            remainingAmount: 'remaining_amount',
            customerName: 'customer_name',
            customerId: 'customer_id',
            tableNumber: 'table_number',
            earnedPoints: 'earned_points',
            createdBy: 'created_by',
            paidAt: 'paid_at',
            discountAmount: 'discount_amount',
            appliedPromotionId: 'applied_promotion_id',
            appliedPromotionTitle: 'applied_promotion_title',
        };
        for (const [key, value] of Object.entries(orderData)) {
            const dbKey = keyMap[key] || key;
            updatePayload[dbKey] = value;
        }
        updatePayload.updated_at = new Date().toISOString();

        await supabase.from('orders').update(updatePayload).eq('id', orderId);
        await logActivity(tenantId, userEmail, userId, 'UPDATE_ORDER_DETAILS', `Order ${orderId} details updated`, { orderId, updatePayload });
    },

    async findActiveOrderForTable(tenantId, tableNumber) {
        if (!tenantId || !tableNumber) return null;
        const { data } = await supabase
            .from('orders')
            .select('*')
            .eq('tenant_id', tenantId)
            .eq('table_number', tableNumber)
            .in('status', ['pending', 'delivered'])
            .limit(1);

        if (!data || data.length === 0) return null;
        return toOrder(data[0]);
    },

    async createOrder(orderData, tenantId, userId, userEmail) {
        // Check if there's an active order for this table to append
        if (orderData.tableNumber && orderData.tableNumber !== 'Manuel') {
            const activeOrder = await this.findActiveOrderForTable(tenantId, orderData.tableNumber);
            if (activeOrder) {
                // Append items
                const updatedItems = [...activeOrder.items];
                orderData.items.forEach(newItem => {
                    const existing = updatedItems.find(i => i.prodId === newItem.prodId);
                    if (existing) {
                        existing.quantity += newItem.quantity;
                    } else {
                        updatedItems.push(newItem);
                    }
                });

                const addedAmount = Number(orderData.totalAmount || 0);
                const newTotal = Number(activeOrder.totalAmount || 0) + addedAmount;
                const newRemaining = Number(activeOrder.remainingAmount ?? activeOrder.totalAmount ?? 0) + addedAmount;

                await supabase
                    .from('orders')
                    .update({
                        items: updatedItems,
                        total_amount: newTotal,
                        remaining_amount: newRemaining,
                        updated_at: new Date().toISOString(),
                    })
                    .eq('id', activeOrder.id);

                await logActivity(tenantId, userEmail, userId, 'APPEND_ORDER', `Added items to existing order: ${activeOrder.id}`, {
                    orderId: activeOrder.id,
                    addedItems: orderData.items,
                    newTotal: newTotal,
                    tableNumber: activeOrder.tableNumber
                });
                return { id: activeOrder.id, appended: true };
            }
        }

        const { data, error } = await supabase
            .from('orders')
            .insert({
                tenant_id: tenantId,
                customer_id: orderData.customerId || null,
                customer_name: orderData.customerName || null,
                table_number: orderData.tableNumber || null,
                items: orderData.items,
                total_amount: orderData.totalAmount,
                remaining_amount: orderData.remainingAmount ?? orderData.totalAmount,
                subtotal: orderData.subtotal ?? orderData.totalAmount,
                discount_amount: orderData.discountAmount ?? 0,
                applied_promotion_id: orderData.appliedPromotionId || null,
                applied_promotion_title: orderData.appliedPromotionTitle || null,
                payments: orderData.payments || null,
                earned_points: orderData.earnedPoints ?? 0,
                created_by: userId,
                status: 'pending',
                timestamp: new Date().toISOString(),
            })
            .select('id')
            .single();

        if (error) throw error;

        await logActivity(tenantId, userEmail, userId, 'CREATE_ORDER', `Order created: ${data.id}`, {
            orderId: data.id,
            items: orderData.items,
            totalAmount: orderData.totalAmount,
            tableNumber: orderData.tableNumber,
            customerName: orderData.customerName
        });
        return { id: data.id, appended: false };
    }
};
